import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ArrowLeft, MapPin, Building2, Calendar, Phone, MessageSquare, ShieldCheck,
  Check, CheckCircle2, Loader2, X, ChevronLeft, ChevronRight, Layers, FileText, Sparkles, Send, Mail,
  ArrowUpDown, ArrowUp, ArrowDown, Eye, Filter, Search, Compass, Grid, ExternalLink,
  ShoppingBag, Plane, GraduationCap, School, Globe
} from "lucide-react";
import { toast } from "sonner";
import NavbarHeader from "@/components/NavbarHeader";
import ProjectMap from "@/components/ProjectMap";
import { useSgdChfRate, formatPriceFrom, formatPsf, convertSgdText } from "@/lib/currency";
import { useLaunchCoordinates } from "@/lib/launchLocation";
import RichWriteup from "@/components/RichWriteup";
import { API } from "@/lib/api";

const BASE_URL = API.replace(/\/api$/, "");

function cleanDriveUrl(url, isThumb = false) {
  if (!url) return "";
  const s = String(url).trim();
  if (s.startsWith("/api/launches/media//") || s.includes("/upload/broke/")) {
    return "";
  }
  const param = isThumb ? "?thumb=true" : "";
  if (s.startsWith("/api/")) {
    const hasThumb = s.includes("thumb=true");
    if (isThumb && !hasThumb) {
      return `${BASE_URL}${s}${s.includes("?") ? "&thumb=true" : "?thumb=true"}`;
    }
    return `${BASE_URL}${s}`;
  }
  if (s.includes("drive.google.com") || s.includes("docs.google.com")) {
    const match = s.match(/(?:id=|\/d\/|\/file\/d\/)([a-zA-Z0-9_-]{25,})/);
    if (match) return `${API}/launches/media/${match[1]}${param}`;
  }
  if (s.length >= 25 && !s.startsWith("http") && !s.includes("/")) {
    return `${API}/launches/media/${s}${param}`;
  }
  return s;
}

function normalizeTenure(raw) {
  if (!raw) return "99-year Leasehold";
  const t = String(raw).toLowerCase().trim();
  if (t.includes("freehold") || t.includes("fee simple") || t.includes("999")) return "Freehold / 999-year";
  return "99-year Leasehold";
}

function formatTenureWithYear(raw) {
  if (!raw) return "99-year Leasehold";
  const s = String(raw).trim();
  const norm = normalizeTenure(s);
  if (norm === "Freehold / 999-year") return "Freehold / 999-year";
  const match = s.match(/\b(19\d\d|20\d\d)\b/);
  if (match) return `${norm} from ${match[1]}`;
  return norm;
}

const DISTRICT_NAME_MAP = {
  D01: "Raffles Place / Marina Bay / Boat Quay",
  D02: "Anson / Tanjong Pagar / Chinatown",
  D03: "Queenstown / Tiong Bahru / Alexandra",
  D04: "Sentosa / Telok Blangah / HarbourFront",
  D05: "Buona Vista / West Coast / Clementi",
  D06: "City Hall / Clarke Quay",
  D07: "Middle Road / Golden Mile / Bugis",
  D08: "Little India / Farrer Park",
  D09: "Orchard / Cairnhill / River Valley",
  D10: "Tanglin / Holland / Nassim / Bukit Timah",
  D11: "Newton / Novena / Dunearn",
  D12: "Toa Payoh / Balestier / Serangoon",
  D13: "MacPherson / Potong Pasir / Braddell",
  D14: "Eunos / Geylang / Paya Lebar",
  D15: "East Coast / Marine Parade / Katong",
  D16: "Bedok / Upper East Coast / Bayshore",
  D17: "Changi / Loyang",
  D18: "Tampines / Pasir Ris",
  D19: "Serangoon / Hougang / Sengkang / Punggol",
  D20: "Bishan / Ang Mo Kio / Thomson",
  D21: "Upper Bukit Timah / Clementi Park",
  D22: "Boon Lay / Jurong / Tuas",
  D23: "Bukit Batok / Hillview / Choa Chu Kang",
  D24: "Lim Chu Kang / Tengah",
  D25: "Woodlands / Kranji",
  D26: "Mandai / Upper Thomson",
  D27: "Yishun / Sembawang",
  D28: "Seletar / Yio Chu Kang",
};

function formatDistrictLabel(raw) {
  if (!raw) return "Singapore";
  const s = String(raw).trim();
  const match = s.match(/D?0*(\d+)/i);
  if (match) {
    const num = parseInt(match[1], 10);
    const code = `D${num < 10 ? "0" + num : num}`;
    const name = DISTRICT_NAME_MAP[code];
    if (name) {
      if (s.includes(" - ")) return s;
      return `${code} - ${name}`;
    }
    return s.startsWith("D") ? s : `D${num < 10 ? "0" + num : num}`;
  }
  return s;
}

export default function LaunchDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const chfRate = useSgdChfRate();
  const [launch, setLaunch] = useState(null);
  const [loading, setLoading] = useState(true);
  const coords = useLaunchCoordinates(launch);

  // Lightbox State
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeImgIdx, setActiveImgIdx] = useState(0);

  // Unit Section Tabs & Modal State
  const [unitSectionTab, setUnitSectionTab] = useState("mix"); // "mix" or "actual"
  const [unitsModalOpen, setUnitsModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [unitSearch, setUnitSearch] = useState("");
  const [sortField, setSortField] = useState("unitNumber");
  const [sortOrder, setSortOrder] = useState("asc");

  // Single Floorplan Preview Lightbox
  const [floorplanPreviewUrl, setFloorplanPreviewUrl] = useState(null);
  const [activeFpTab, setActiveFpTab] = useState("All");

  // Enquiry Form State
  const [form, setForm] = useState({ name: "", email: "", phone: "", unitType: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submittedData, setSubmittedData] = useState(null);

  const handleImgFallback = (e) => {
    e.target.onerror = null;
    e.target.src = "data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='600' viewBox='0 0 800 600'%3E%3Crect width='800' height='600' fill='%230f172a'/%3E%3Cg fill='none' stroke='%23334155' stroke-width='3'%3E%3Crect x='300' y='180' width='200' height='260' rx='8'/%3E%3Cpath d='M340 220 h30 v30 h-30 z M430 220 h30 v30 h-30 z M340 280 h30 v30 h-30 z M430 280 h30 v30 h-30 z M340 340 h30 v30 h-30 z M430 340 h30 v30 h-30 z M370 440 v-40 h60 v40'/%3E%3C/g%3E%3Ctext x='50%25' y='80%25' dominant-baseline='middle' text-anchor='middle' fill='%2364748b' font-family='sans-serif' font-size='16'%3EArchitectural Render Unavailable%3C/text%3E%3C/svg%3E";
  };

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        const cachedStr = sessionStorage.getItem("asp_launches_cache");
        if (cachedStr) {
          const cachedList = JSON.parse(cachedStr);
          const found = cachedList.find((l) => l && (l.slug === slug || l.slug?.current === slug || l._id === slug));
          if (found) {
            setLaunch(found);
            setLoading(false);
          }
        }
      } catch (_e) {}

      try {
        const res = await api.get(`/launches/${slug}`);
        const dataLaunch = res.data?.launch || (res.data?.title ? res.data : null);
        if (dataLaunch) {
          setLaunch(dataLaunch);
        }
      } catch (_err) {
        if (!launch) toast.error("Project details not found.");
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Consolidate hero images, renders & site plans into allImages array (excluding floorplans)
  const allImages = useMemo(() => {
    if (!launch) return [];
    const list = [];
    if (launch.heroImageUrl) list.push(cleanDriveUrl(launch.heroImageUrl));
    if (Array.isArray(launch.imagesUrls)) {
      launch.imagesUrls.forEach((img) => {
        const cleaned = cleanDriveUrl(img);
        if (cleaned && !list.includes(cleaned)) list.push(cleaned);
      });
    }
    if (Array.isArray(launch.siteplansUrls)) {
      launch.siteplansUrls.forEach((sp) => {
        const cleaned = cleanDriveUrl(sp);
        if (cleaned && !list.includes(cleaned)) list.push(cleaned);
      });
    }
    return list;
  }, [launch]);

  // Actual available units list from unit spreadsheet data
  const availableUnitsList = useMemo(() => {
    if (!launch || !Array.isArray(launch.unitsList)) return [];
    return launch.unitsList.filter((u) => u && (u.isAvailable || u.status === "Available" || String(u.status).toLowerCase() !== "sold"));
  }, [launch]);

  const categoriesList = useMemo(() => {
    const set = new Set(availableUnitsList.map((u) => u.bedroomCategory || u.bedroomType || "Other Layouts"));
    return ["All", ...Array.from(set)];
  }, [availableUnitsList]);

  const filteredUnits = useMemo(() => {
    let result = [...availableUnitsList];
    if (selectedCategory !== "All") {
      result = result.filter(
        (u) => (u.bedroomCategory || u.bedroomType) === selectedCategory
      );
    }
    if (unitSearch.trim()) {
      const q = unitSearch.toLowerCase().trim();
      result = result.filter(
        (u) =>
          String(u.unitNumber || "").toLowerCase().includes(q) ||
          String(u.stack || "").toLowerCase().includes(q) ||
          String(u.bedroomType || "").toLowerCase().includes(q)
      );
    }
    result.sort((a, b) => {
      let valA = a[sortField];
      let valB = b[sortField];
      if (valA === undefined || valA === null) valA = "";
      if (valB === undefined || valB === null) valB = "";
      if (typeof valA === "number" && typeof valB === "number") {
        return sortOrder === "asc" ? valA - valB : valB - valA;
      }
      return sortOrder === "asc"
        ? String(valA).localeCompare(String(valB))
        : String(valB).localeCompare(String(valA));
    });
    return result;
  }, [availableUnitsList, selectedCategory, unitSearch, sortField, sortOrder]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const openUnitsModalForCategory = (cat) => {
    setSelectedCategory(cat);
    setUnitsModalOpen(true);
  };

  const handleEnquire = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please enter your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/launches/enquire", {
        ...form,
        listingTitle: launch.title,
      });
      if (res.data && res.data.ok) {
        toast.success(res.data.message || "Enquiry submitted successfully!");
        setSubmittedData({
          ...form,
          listingTitle: launch.title,
          submittedAt: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short' }),
        });
        setForm({ name: "", email: "", phone: "", unitType: "", message: "" });
      } else {
        toast.error("Failed to submit enquiry. Please try again.");
      }
    } catch (_e) {
      toast.error("Failed to submit enquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F3EE] text-[#0A1128] p-6 max-w-7xl mx-auto space-y-6">
        <Skeleton className="h-8 w-32 bg-slate-200" />
        <Skeleton className="h-96 w-full rounded-2xl bg-slate-200" />
        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2 space-y-4">
            <Skeleton className="h-8 w-3/4 bg-slate-200" />
            <Skeleton className="h-32 w-full bg-slate-200" />
          </div>
          <Skeleton className="h-80 w-full bg-slate-200" />
        </div>
      </div>
    );
  }

  if (!launch) {
    return (
      <div className="min-h-screen bg-[#F5F3EE] text-[#0A1128] flex flex-col items-center justify-center p-6 text-center">
        <Building2 className="w-16 h-16 text-slate-400 mb-4" />
        <h2 className="text-2xl font-serif-display">Project Not Found</h2>
        <p className="text-sm text-slate-600 mt-2 mb-6">The requested new launch project could not be found.</p>
        <Link to="/launches">
          <Button className="bg-[#0A1128] text-white font-semibold hover:bg-[#152243]">
            <ArrowLeft className="w-4 h-4 mr-2" /> Return to Catalog
          </Button>
        </Link>
      </div>
    );
  }

  const agent = launch.agent || {
    name: "Alpine Straits Partner Desk",
    initials: "AS",
    phone: "6588841193",
    whatsapp: "6588841193",
    email: "partners@alpinestraitspartners.com.sg",
  };

  return (
    <div className="min-h-screen bg-[#F5F3EE] text-[#0A1128]">
      <NavbarHeader />
      {/* Top Header Navigation */}
      <div className="border-b border-[#E7E2D6] bg-white py-4 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link
            to="/launches"
            className="inline-flex items-center text-xs font-medium text-slate-600 hover:text-[#C5A880] transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to New Launches
          </Link>
        </div>
      </div>

      {/* Main Container */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8 space-y-8">
        {/* Project Title Header */}
        <div>
          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600 mb-2">
            <span className="bg-[#0A1128] text-[#C5A880] font-semibold px-2.5 py-0.5 rounded border border-[#0A1128]">
              {formatDistrictLabel(launch.district)}
            </span>
            <span className="bg-white text-slate-700 px-2.5 py-0.5 rounded border border-[#E7E2D6] font-medium">{formatTenureWithYear(launch.tenure || launch.rawTenure)}</span>
            {launch.topDate && <span className="bg-white text-slate-700 px-2.5 py-0.5 rounded border border-[#E7E2D6] font-medium">TOP {launch.topDate}</span>}
          </div>

          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
            <div>
              <h1 className="font-serif-display text-3xl lg:text-5xl font-medium text-[#0A1128] tracking-tight">
                {launch.title}
              </h1>
              <p className="text-sm text-slate-600 mt-2 flex items-center gap-1.5">
                <MapPin className="w-4 h-4 text-[#C5A880] shrink-0" />
                {launch.location}
              </p>
            </div>

            <div className="text-left lg:text-right bg-white p-4 rounded-xl border border-[#E7E2D6] shadow-sm">
              <div className="text-xs text-slate-500 uppercase tracking-wider font-medium">Prices Starting From</div>
              <div className="font-serif-display text-3xl text-[#0A1128] font-semibold mt-0.5">
                {formatPriceFrom(launch.priceFrom, chfRate)}
              </div>
              {launch.psfFrom && <div className="text-xs text-slate-500 mt-1">{formatPsf(launch.psfFrom, chfRate)}</div>}
              <div className="text-[10px] text-slate-400 mt-1">Converted from SGD &middot; 1 SGD = {chfRate.toFixed(4)} CHF</div>
            </div>
          </div>
        </div>

        {/* Hero Gallery Carousel Slider */}
        <div className="relative rounded-2xl overflow-hidden border border-[#E7E2D6] bg-white shadow-md">
          <div
            onClick={() => setLightboxOpen(true)}
            className="cursor-pointer group relative h-[380px] lg:h-[500px] overflow-hidden bg-slate-100"
          >
            {allImages[activeImgIdx] ? (
              <img
                src={cleanDriveUrl(allImages[activeImgIdx], true)}
                alt={launch.title}
                decoding="async"
                fetchPriority="high"
                onError={handleImgFallback}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-slate-400">
                <Building2 className="w-16 h-16" />
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A1128]/70 via-transparent to-transparent opacity-80 pointer-events-none" />
            
            {/* Top Left Photo Counter Badge */}
            <div className="absolute top-4 left-4 bg-[#0A1128]/85 text-xs text-white px-3 py-1.5 rounded-lg border border-white/20 backdrop-blur font-medium z-10">
              Photo {activeImgIdx + 1} of {allImages.length}
            </div>

            {/* Bottom Right Fullscreen Button */}
            <div className="absolute bottom-4 right-4 bg-[#0A1128]/85 text-xs text-white px-3.5 py-2 rounded-lg border border-white/20 backdrop-blur flex items-center gap-2 font-medium z-10">
              <Sparkles className="w-3.5 h-3.5 text-[#C5A880]" /> Click for Fullscreen Gallery ({allImages.length} photos)
            </div>

            {/* Hero Carousel Navigation Overlay Arrows */}
            {allImages.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveImgIdx((prev) => (prev > 0 ? prev - 1 : allImages.length - 1));
                  }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-[#0A1128]/75 hover:bg-[#0A1128] text-white flex items-center justify-center border border-white/30 backdrop-blur transition-all hover:scale-110 shadow-lg z-20"
                  aria-label="Previous image"
                >
                  <ChevronLeft className="w-6 h-6 text-[#C5A880]" />
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveImgIdx((prev) => (prev < allImages.length - 1 ? prev + 1 : 0));
                  }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-[#0A1128]/75 hover:bg-[#0A1128] text-white flex items-center justify-center border border-white/30 backdrop-blur transition-all hover:scale-110 shadow-lg z-20"
                  aria-label="Next image"
                >
                  <ChevronRight className="w-6 h-6 text-[#C5A880]" />
                </button>
              </>
            )}
          </div>

          {/* Thumbnail Strip */}
          {allImages.length > 1 && (
            <div className="p-3 bg-white flex items-center gap-2.5 overflow-x-auto border-t border-[#E7E2D6]">
              {allImages.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveImgIdx(idx)}
                  className={`relative w-20 h-14 rounded-lg overflow-hidden shrink-0 border-2 transition-all ${
                    idx === activeImgIdx ? "border-[#C5A880] scale-105 shadow-sm" : "border-[#E7E2D6] opacity-60 hover:opacity-100"
                  }`}
                >
                  <img
                    src={cleanDriveUrl(img, true)}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    onError={handleImgFallback}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Content & Form Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Specs & Unit Mix */}
          <div className="lg:col-span-2 space-y-8">
            {/* Quick Specs Grid */}
            <div className="bg-white border border-[#E7E2D6] rounded-xl p-6 grid grid-cols-1 sm:grid-cols-3 gap-4 shadow-sm">
              <div>
                <div className="text-[11px] text-slate-500 uppercase tracking-wider font-medium">TOP Completion</div>
                <div className="text-sm font-semibold text-[#0A1128] mt-1">{launch.topDate || "TBC"}</div>
              </div>
              <div>
                <div className="text-[11px] text-slate-500 uppercase tracking-wider font-medium">Tenure</div>
                <div className="text-sm font-semibold text-[#0A1128] mt-1">{formatTenureWithYear(launch.tenure || launch.rawTenure)}</div>
              </div>
              <div>
                <div className="text-[11px] text-slate-500 uppercase tracking-wider font-medium">Available Units</div>
                <div className="text-sm font-semibold text-[#0A1128] mt-1">
                  {launch.availableUnits
                    ? `${launch.availableUnits} units available`
                    : launch.unitsRemaining
                    ? `${launch.unitsRemaining} units available`
                    : launch.totalUnits
                    ? `${launch.totalUnits} units available`
                    : "TBC"}
                </div>
              </div>
            </div>

            {/* Description */}
            {launch.description && (
              <div className="bg-white border border-[#E7E2D6] rounded-xl p-6 shadow-sm">
                <h3 className="font-serif-display text-xl text-[#0A1128] mb-3 font-medium">Project Overview</h3>
                <RichWriteup text={launch.description} />
              </div>
            )}

            {/* Tabbed Unit Availability & Pricing Section */}
            <div className="bg-white border border-[#E7E2D6] rounded-xl p-6 overflow-hidden shadow-sm">
              {/* Header with Navigation Tabs */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5 border-b border-[#E7E2D6] pb-4">
                <div>
                  <h3 className="font-serif-display text-xl text-[#0A1128] font-medium">Unit Availability & Pricing</h3>
                  <p className="text-xs text-slate-500 mt-0.5">Explore category starting prices or browse actual individual units for sale with live PSF & floorplan links.</p>
                </div>

                {/* Tab Switcher */}
                <div className="flex items-center gap-1.5 bg-[#F5F3EE] p-1 rounded-xl border border-[#E7E2D6] shrink-0">
                  <button
                    type="button"
                    onClick={() => setUnitSectionTab("mix")}
                    className={`px-3.5 py-1.5 text-xs font-semibold rounded-lg transition-all ${
                      unitSectionTab === "mix"
                        ? "bg-[#0A1128] text-[#C5A880] shadow-sm"
                        : "text-slate-600 hover:text-[#0A1128]"
                    }`}
                  >
                    Unit Mix Breakdown
                  </button>
                  <button
                    type="button"
                    onClick={() => setUnitSectionTab("actual")}
                    className={`px-3.5 py-1.5 text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5 ${
                      unitSectionTab === "actual"
                        ? "bg-[#0A1128] text-[#C5A880] shadow-sm"
                        : "text-slate-600 hover:text-[#0A1128]"
                    }`}
                  >
                    <Grid className="w-3.5 h-3.5 text-[#C5A880]" />
                    Actual Units for Sale ({availableUnitsList.length})
                  </button>
                </div>
              </div>

              {/* TAB 1: Aggregated Unit Mix Breakdown */}
              {unitSectionTab === "mix" && Array.isArray(launch.unitMix) && launch.unitMix.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs cursor-pointer">
                    <thead>
                      <tr className="border-b border-[#E7E2D6] bg-[#F5F3EE] text-slate-600 font-medium">
                        <th className="p-3">Unit Type</th>
                        <th className="p-3">Available Mix</th>
                        <th className="p-3">Price From</th>
                        <th className="p-3">Status</th>
                        <th className="p-3 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E7E2D6]">
                      {launch.unitMix.map((row, i) => (
                        <tr
                          key={i}
                          onClick={() => {
                            setUnitSectionTab("actual");
                            setSelectedCategory(row.type);
                          }}
                          className="hover:bg-slate-50 transition-colors group"
                        >
                          <td className="p-3 font-semibold text-[#0A1128] flex items-center gap-2">
                            {row.type}
                            {row.floorplanUrl && (
                              <span className="text-[10px] text-[#C5A880] bg-[#C5A880]/10 px-1.5 py-0.5 rounded font-normal">
                                Floorplan Available
                              </span>
                            )}
                          </td>
                          <td className="p-3 text-slate-600">
                            {row.units ? `${row.units} units` : row.size || "Available"}
                          </td>
                          <td className="p-3 font-semibold text-[#0A1128]">
                            {formatPriceFrom(row.priceFrom, chfRate)}
                          </td>
                          <td className="p-3">
                            <span className="inline-block px-2 py-0.5 rounded text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium">
                              {row.availability || "Available"}
                            </span>
                          </td>
                          <td className="p-3 text-right text-xs font-semibold text-[#C5A880] group-hover:underline">
                            View Units →
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* TAB 2: Embedded Table of Actual Individual Units for Sale */}
              {unitSectionTab === "actual" && (
                <div className="space-y-4">
                  {/* Category Filter Pills & Search */}
                  <div className="p-3.5 bg-[#F5F3EE] rounded-xl border border-[#E7E2D6] flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    {/* Category Filter Pills */}
                    <div className="flex flex-wrap items-center gap-1.5">
                      {categoriesList.map((cat) => (
                        <button
                          key={cat}
                          onClick={() => setSelectedCategory(cat)}
                          className={`text-xs px-3 py-1.5 rounded-lg transition-colors font-medium ${
                            selectedCategory === cat
                              ? "bg-[#0A1128] text-white shadow-sm"
                              : "bg-white text-slate-700 hover:bg-slate-100 border border-[#E7E2D6]"
                          }`}
                        >
                          {cat}
                        </button>
                      ))}
                    </div>

                    {/* Unit Search */}
                    <div className="relative w-full sm:w-60 shrink-0">
                      <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
                      <input
                        type="text"
                        placeholder="Search unit # or stack..."
                        value={unitSearch}
                        onChange={(e) => setUnitSearch(e.target.value)}
                        className="w-full pl-9 pr-3 py-1.5 text-xs bg-white border border-[#E7E2D6] rounded-lg outline-none focus:border-[#C5A880]"
                      />
                    </div>
                  </div>

                  {/* Embedded Table */}
                  {filteredUnits.length > 0 ? (
                    <div className="overflow-x-auto border border-[#E7E2D6] rounded-xl">
                      <table className="w-full text-left text-xs">
                        <thead className="bg-[#F5F3EE] border-b border-[#E7E2D6] text-slate-700 font-semibold select-none">
                          <tr>
                            <th
                              onClick={() => handleSort("unitNumber")}
                              className="p-3 cursor-pointer hover:bg-slate-200/50 transition-colors"
                            >
                              <div className="flex items-center gap-1">
                                Unit # <ArrowUpDown className="w-3 h-3 text-slate-400" />
                              </div>
                            </th>
                            <th className="p-3">Bedroom Type</th>
                            <th
                              onClick={() => handleSort("floor")}
                              className="p-3 cursor-pointer hover:bg-slate-200/50 transition-colors"
                            >
                              <div className="flex items-center gap-1">
                                Floor / Stack <ArrowUpDown className="w-3 h-3 text-slate-400" />
                              </div>
                            </th>
                            <th
                              onClick={() => handleSort("sqft")}
                              className="p-3 cursor-pointer hover:bg-slate-200/50 transition-colors"
                            >
                              <div className="flex items-center gap-1">
                                Sqft <ArrowUpDown className="w-3 h-3 text-slate-400" />
                              </div>
                            </th>
                            <th
                              onClick={() => handleSort("price")}
                              className="p-3 cursor-pointer hover:bg-slate-200/50 transition-colors"
                            >
                              <div className="flex items-center gap-1">
                                Price (CHF) <ArrowUpDown className="w-3 h-3 text-slate-400" />
                              </div>
                            </th>
                            <th
                              onClick={() => handleSort("psf")}
                              className="p-3 cursor-pointer hover:bg-slate-200/50 transition-colors"
                            >
                              <div className="flex items-center gap-1">
                                PSF <ArrowUpDown className="w-3 h-3 text-slate-400" />
                              </div>
                            </th>
                            <th className="p-3">Status</th>
                            <th className="p-3 text-right">Floorplan</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-[#E7E2D6]">
                          {filteredUnits.map((u, idx) => (
                            <tr key={idx} className="hover:bg-slate-50 transition-colors">
                              <td className="p-3 font-semibold text-[#0A1128]">{u.unitNumber}</td>
                              <td className="p-3 text-slate-700 font-medium">{u.bedroomType}</td>
                              <td className="p-3 text-slate-600">
                                {u.floor && u.stack ? `L${u.floor} / Stk ${u.stack}` : u.floor ? `Level ${u.floor}` : `Stack ${u.stack || '-'}`}
                              </td>
                              <td className="p-3 font-medium text-slate-800">
                                {u.sqft ? `${u.sqft.toLocaleString()} sqft` : 'TBC'}
                              </td>
                              <td className="p-3 font-semibold text-[#0A1128]">
                                {u.priceStr ? convertSgdText(u.priceStr, chfRate) : 'Price on Request'}
                              </td>
                              <td className="p-3 text-slate-600 font-medium">
                                {u.psfStr ? convertSgdText(u.psfStr, chfRate) : '-'}
                              </td>
                              <td className="p-3">
                                <span className="inline-block px-2 py-0.5 rounded text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium">
                                  {u.status || "Available"}
                                </span>
                              </td>
                              <td className="p-3 text-right">
                                {u.floorplanUrl ? (
                                  <button
                                    onClick={() => setFloorplanPreviewUrl(cleanDriveUrl(u.floorplanUrl))}
                                    className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold text-[#C5A880] bg-[#C5A880]/10 hover:bg-[#C5A880]/20 rounded border border-[#C5A880]/30 transition-colors"
                                  >
                                    <Eye className="w-3 h-3" /> View Floorplan
                                  </button>
                                ) : (
                                  <span className="text-[11px] text-slate-400 italic">On Request</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="py-12 bg-[#F9FAFB] rounded-xl border border-[#E7E2D6] text-center p-6 space-y-3">
                      <Grid className="w-10 h-10 text-slate-400 mx-auto" />
                      <h4 className="text-sm font-semibold text-[#0A1128]">
                        {availableUnitsList.length > 0
                          ? "No units match your search filter"
                          : "Full Unit Availability Matrix Available on Request"}
                      </h4>
                      <p className="text-xs text-slate-600 max-w-md mx-auto">
                        {availableUnitsList.length > 0
                          ? "Try clearing your search query or selecting a different bedroom type."
                          : `The unit-by-unit listing matrix for ${launch.title} is available upon request. Register below to receive live availability updates.`}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Site Plans Section */}
            {Array.isArray(launch.siteplansUrls) && launch.siteplansUrls.length > 0 && (
              <div className="bg-white border border-[#E7E2D6] rounded-xl p-6 shadow-sm">
                <h3 className="font-serif-display text-xl text-[#0A1128] mb-4 flex items-center gap-2 font-medium">
                  <Compass className="w-5 h-5 text-[#C5A880]" /> Site Plans & Development Layouts ({launch.siteplansUrls.length})
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {launch.siteplansUrls.map((spUrl, i) => (
                    <div
                      key={i}
                      onClick={() => setFloorplanPreviewUrl(cleanDriveUrl(spUrl))}
                      className="group cursor-pointer bg-[#F9FAFB] border border-[#E7E2D6] rounded-xl overflow-hidden p-3 hover:border-[#C5A880] transition-colors relative"
                    >
                      <img
                        src={cleanDriveUrl(spUrl, true)}
                        alt={`Site Plan ${i + 1}`}
                        loading="lazy"
                        decoding="async"
                        onError={handleImgFallback}
                        className="w-full h-48 object-contain rounded"
                      />
                      <div className="mt-2 text-center text-xs font-medium text-slate-700 group-hover:text-[#C5A880] flex items-center justify-center gap-1">
                        <Eye className="w-3.5 h-3.5" /> View Site Plan & Master Layout
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}



            {/* OneMap Interactive Location Map Section */}
            <div className="bg-white border border-[#E7E2D6] rounded-xl p-6 shadow-sm overflow-hidden space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-[#E7E2D6] pb-3">
                <div>
                  <h3 className="font-serif-display text-xl text-[#0A1128] font-medium flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-[#C5A880]" /> OneMap Interactive Location Layout
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Official Singapore Land Authority (SLA) OneMap GIS layout for {launch.title}.
                  </p>
                </div>
                {(launch.address || launch.location) && (
                  <span className="text-xs text-[#0A1128] font-semibold bg-[#F5F3EE] border border-[#E7E2D6] px-3 py-1 rounded-lg self-start sm:self-auto">
                    📍 {launch.address || launch.location}
                  </span>
                )}
              </div>

              {/* Interactive OneMap Leaflet Map with Red Pin Marker */}
              <ProjectMap
                lat={coords?.lat}
                lng={coords?.lng}
                title={launch.title}
                address={launch.address || launch.location}
              />

              <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500 pt-1">
                <span>District: <strong className="text-[#0A1128]">{launch.district}</strong></span>
                <span>Coordinates: <strong className="text-[#0A1128]">{coords ? `${coords.lat.toFixed(4)}° N, ${coords.lng.toFixed(4)}° E` : "Locating…"}</strong></span>
                <a
                  href={coords
                    ? `https://www.google.com/maps/search/?api=1&query=${coords.lat},${coords.lng}`
                    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent((launch.title || launch.address) + " Singapore")}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#C5A880] hover:underline font-semibold flex items-center gap-1"
                >
                  Open in Google Maps <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>

            {/* Foreigner & Expat Distance & Nearby Amenities Section */}
            <div className="bg-white border border-[#E7E2D6] rounded-xl p-6 shadow-sm space-y-4">
              <div className="border-b border-[#E7E2D6] pb-3">
                <h3 className="font-serif-display text-xl text-[#0A1128] font-medium flex items-center gap-2">
                  <Compass className="w-5 h-5 text-[#C5A880]" /> Key Transport & Foreigner Expat Proximity Metrics
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Direct travel times to Singapore's Central Business District, International Schools, Airport & Premium Shopping Hubs.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {(launch.keyDistances || [
                  { name: "Raffles Place / Marina Bay CBD", distStr: "2.4 km", driveTime: "~8 mins drive", transitTime: "~12 mins MRT", icon: "Building2" },
                  { name: "Orchard Road Shopping Belt", distStr: "4.1 km", driveTime: "~12 mins drive", transitTime: "~16 mins MRT", icon: "ShoppingBag" },
                  { name: "Changi International Airport (SIN)", distStr: "18.5 km", driveTime: "~22 mins drive", transitTime: "~38 mins MRT", icon: "Plane" },
                  { name: "Marina Bay Sands & Gardens by the Bay", distStr: "1.8 km", driveTime: "~5 mins drive", transitTime: "~8 mins MRT", icon: "Sparkles" },
                  { name: "Swiss Club & Swiss International School", distStr: "8.2 km", driveTime: "~15 mins drive", transitTime: "~25 mins MRT", icon: "GraduationCap" },
                  { name: "Tanglin Trust & UWCSEA Intl Schools Hub", distStr: "5.4 km", driveTime: "~12 mins drive", transitTime: "~20 mins MRT", icon: "School" }
                ]).map((item, idx) => (
                  <div key={idx} className="bg-[#F9FAFB] border border-[#E7E2D6] rounded-xl p-4 hover:border-[#C5A880] transition-colors space-y-2">
                    <div className="flex items-center justify-between text-xs text-slate-500">
                      <span className="font-medium text-[#0A1128] truncate pr-2">{item.name}</span>
                      <span className="bg-[#C5A880]/15 text-[#0A1128] px-2 py-0.5 rounded font-semibold shrink-0 text-[11px]">
                        {item.distStr}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-[11px] pt-1 text-slate-600 border-t border-slate-200">
                      <span className="flex items-center gap-1 text-[#0A1128] font-medium">🚗 {item.driveTime}</span>
                      <span className="flex items-center gap-1 text-slate-500">🚆 {item.transitTime}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Sidebar: Agent Contact & Registration Form */}
          <div className="space-y-6">
            {/* Agent Desk Card */}
            <div className="bg-white border border-[#E7E2D6] rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-3.5 pb-4 border-b border-[#E7E2D6]">
                <div className="w-12 h-12 rounded-full bg-[#0A1128] border border-[#C5A880] flex items-center justify-center font-bold text-[#C5A880]">
                  {agent.initials || "AS"}
                </div>
                <div>
                  <h4 className="font-medium text-[#0A1128] text-base">{agent.name}</h4>
                  <p className="text-xs text-slate-500">Singapore Partner Desk</p>
                </div>
              </div>

              <div className="mt-4 space-y-2">
                <a
                  href={`https://wa.me/6588841193?text=${encodeURIComponent(`Hi, I am interested in ${launch.title}.`)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold rounded-lg flex items-center justify-center gap-2 transition-colors"
                >
                  <MessageSquare className="w-4 h-4" /> WhatsApp Partner Desk (+65 8884 1193)
                </a>
                <a
                  href="tel:+6588841193"
                  className="w-full py-2.5 bg-[#F5F3EE] hover:bg-[#E7E2D6] border border-[#E7E2D6] text-[#0A1128] text-xs font-semibold rounded-lg flex items-center justify-center gap-2 transition-colors"
                >
                  <Phone className="w-4 h-4" /> Call Partner Desk (+65 8884 1193)
                </a>
                <a
                  href={`mailto:partners@alpinestraitspartners.com.sg?subject=${encodeURIComponent(`Enquiry for ${launch.title}`)}`}
                  className="w-full py-2.5 bg-[#C5A880]/15 hover:bg-[#C5A880]/30 border border-[#C5A880]/40 text-[#0A1128] text-xs font-semibold rounded-lg flex items-center justify-center gap-2 transition-colors"
                >
                  <Mail className="w-4 h-4" /> Direct Email Desk
                </a>
              </div>
            </div>

            {/* Register Interest Form */}
            <div className="bg-white border border-[#E7E2D6] rounded-xl p-6 shadow-sm">
              <h3 className="font-serif-display text-xl text-[#0A1128] mb-1 font-medium">Register VIP Interest</h3>
              <p className="text-xs text-slate-500 mb-4">
                Receive floorplans, e-brochure & direct developer pricing updates for {launch.title}.
              </p>

              {submittedData ? (
                <div className="p-5 bg-[#FAF8F5] border border-[#E7E2D6] rounded-xl space-y-4 animate-in fade-in">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center shrink-0">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <h4 className="text-sm font-serif font-bold text-[#0A1128]">VIP Enquiry Received</h4>
                      <p className="text-xs text-slate-600 mt-0.5">
                        Thank you for your interest in {launch.title}. Our partner desk has received your request and will reach out promptly.
                      </p>
                    </div>
                  </div>

                  <div className="bg-white border border-[#E7E2D6] rounded-lg p-3 space-y-2 text-xs">
                    <div className="flex justify-between border-b border-slate-100 pb-1.5">
                      <span className="text-slate-500">Project</span>
                      <span className="font-medium text-[#0A1128] text-right">{submittedData.listingTitle || launch.title}</span>
                    </div>
                    <div className="flex justify-between border-b border-slate-100 pb-1.5">
                      <span className="text-slate-500">Name</span>
                      <span className="font-medium text-[#0A1128]">{submittedData.name}</span>
                    </div>
                    <div className="flex justify-between border-b border-slate-100 pb-1.5">
                      <span className="text-slate-500">Email</span>
                      <span className="font-medium text-[#0A1128]">{submittedData.email}</span>
                    </div>
                    {submittedData.phone && (
                      <div className="flex justify-between border-b border-slate-100 pb-1.5">
                        <span className="text-slate-500">Phone</span>
                        <span className="font-medium text-[#0A1128]">{submittedData.phone}</span>
                      </div>
                    )}
                    {submittedData.unitType && (
                      <div className="flex justify-between border-b border-slate-100 pb-1.5">
                        <span className="text-slate-500">Preferred Layout</span>
                        <span className="font-medium text-[#0A1128]">{submittedData.unitType}</span>
                      </div>
                    )}
                    {submittedData.message && (
                      <div className="pt-1">
                        <span className="text-slate-500 block mb-1">Message:</span>
                        <p className="text-slate-700 italic bg-slate-50 p-2 rounded text-[11px]">"{submittedData.message}"</p>
                      </div>
                    )}
                    {submittedData.submittedAt && (
                      <div className="text-[10px] text-slate-400 text-right pt-1">
                        Submitted at {submittedData.submittedAt}
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => setSubmittedData(null)}
                    className="w-full py-2.5 bg-white hover:bg-slate-50 border border-slate-300 text-[#0A1128] text-xs font-semibold rounded-lg transition-colors flex items-center justify-center gap-1.5"
                  >
                    Return to Enquiry Form
                  </button>
                </div>
              ) : (
                <form onSubmit={handleEnquire} className="space-y-3.5">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Your Full Name*</label>
                    <Input
                      type="text"
                      required
                      placeholder="John Tan"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="bg-[#F9FAFB] border-slate-300 text-xs text-[#0A1128] placeholder:text-slate-400 focus:bg-white"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Email Address*</label>
                    <Input
                      type="email"
                      required
                      placeholder="john@example.com"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="bg-[#F9FAFB] border-slate-300 text-xs text-[#0A1128] placeholder:text-slate-400 focus:bg-white"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Phone Number</label>
                    <Input
                      type="tel"
                      placeholder="+65 9123 4567"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="bg-[#F9FAFB] border-slate-300 text-xs text-[#0A1128] placeholder:text-slate-400 focus:bg-white"
                    />
                  </div>

                  {Array.isArray(launch.unitMix) && launch.unitMix.length > 0 && (
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1">Preferred Layout</label>
                      <select
                        value={form.unitType}
                        onChange={(e) => setForm({ ...form, unitType: e.target.value })}
                        className="w-full bg-[#F9FAFB] border border-slate-300 rounded-lg px-3 py-2 text-xs text-[#0A1128] outline-none focus:bg-white"
                      >
                        <option value="">Select Interested Unit Type</option>
                        {launch.unitMix.map((u, i) => (
                          <option key={i} value={u.type}>
                            {u.type} (from {u.priceFrom ? convertSgdText(u.priceFrom, chfRate).replace(/^From\s+/i, "") : "TBC"})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Message / Questions</label>
                    <Textarea
                      placeholder="I would like to request floorplans and book a showflat viewing..."
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      className="bg-[#F9FAFB] border-slate-300 text-xs text-[#0A1128] placeholder:text-slate-400 focus:bg-white h-20"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-[#0A1128] text-white font-semibold hover:bg-[#152243] rounded-lg h-10 text-xs transition-colors mt-2 flex items-center justify-center"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Submitting VIP Enquiry...
                      </>
                    ) : (
                      <>
                        Submit VIP Enquiry <Send className="w-3.5 h-3.5 ml-1.5" />
                      </>
                    )}
                  </Button>

                  <div className="text-center pt-2 text-[11px] text-slate-500">
                    Or email directly to{" "}
                    <a
                      href={`mailto:partners@alpinestraitspartners.com.sg?subject=${encodeURIComponent(`VIP Enquiry for ${launch.title}`)}`}
                      className="text-[#C5A880] underline font-medium hover:text-[#0A1128] transition-colors"
                    >
                      partners@alpinestraitspartners.com.sg
                    </a>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox Modal */}
      {lightboxOpen && allImages.length > 0 && (
        <div
          onClick={() => setLightboxOpen(false)}
          className="fixed inset-0 z-[200] bg-black/95 backdrop-blur-md flex items-center justify-center p-4"
        >
          <button
            onClick={() => setLightboxOpen(false)}
            className="fixed top-6 right-6 text-white/80 hover:text-white text-3xl font-light z-[201]"
          >
            <X className="w-8 h-8" />
          </button>

          <div className="fixed top-6 left-6 text-xs text-slate-400 z-[201]">
            {activeImgIdx + 1} / {allImages.length}
          </div>

          <div onClick={(e) => e.stopPropagation()} className="max-w-5xl max-h-[85vh] relative">
            <img
              src={allImages[activeImgIdx]}
              alt=""
              onError={handleImgFallback}
              className="max-w-full max-h-[80vh] object-contain rounded-xl shadow-2xl"
            />
          </div>

          {allImages.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveImgIdx((prev) => (prev > 0 ? prev - 1 : allImages.length - 1));
                }}
                className="fixed left-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center text-xl z-[201]"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveImgIdx((prev) => (prev < allImages.length - 1 ? prev + 1 : 0));
                }}
                className="fixed right-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center text-xl z-[201]"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}
        </div>
      )}

      {/* Units for Sale Modal */}
      {unitsModalOpen && (
        <div
          onClick={() => setUnitsModalOpen(false)}
          className="fixed inset-0 z-[250] bg-black/75 backdrop-blur-sm flex items-center justify-center p-4 lg:p-8"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white border border-[#E7E2D6] rounded-2xl w-full max-w-5xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden text-[#0A1128]"
          >
            {/* Modal Header */}
            <div className="p-6 border-b border-[#E7E2D6] bg-[#0A1128] text-white flex items-center justify-between">
              <div>
                <h3 className="font-serif-display text-xl font-medium flex items-center gap-2">
                  <Grid className="w-5 h-5 text-[#C5A880]" /> {launch.title} - Units Available for Sale
                </h3>
                <p className="text-xs text-slate-300 mt-1">
                  Showing {filteredUnits.length} unsold unit{filteredUnits.length === 1 ? "" : "s"} with live price & PSF breakdown.
                </p>
              </div>
              <button
                onClick={() => setUnitsModalOpen(false)}
                className="text-white/70 hover:text-white p-2 rounded-lg hover:bg-white/10 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Filter & Search Bar */}
            <div className="p-4 bg-[#F5F3EE] border-b border-[#E7E2D6] flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              {/* Category Pills */}
              <div className="flex flex-wrap items-center gap-1.5 overflow-x-auto py-1">
                {categoriesList.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={`text-xs px-3 py-1.5 rounded-lg transition-colors font-medium ${
                      selectedCategory === cat
                        ? "bg-[#0A1128] text-white shadow-sm"
                        : "bg-white text-slate-700 hover:bg-slate-100 border border-[#E7E2D6]"
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>

              {/* Unit Search */}
              <div className="relative w-full sm:w-64 shrink-0">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
                <input
                  type="text"
                  placeholder="Search unit # or stack..."
                  value={unitSearch}
                  onChange={(e) => setUnitSearch(e.target.value)}
                  className="w-full pl-9 pr-3 py-1.5 text-xs bg-white border border-[#E7E2D6] rounded-lg outline-none focus:border-[#C5A880]"
                />
              </div>
            </div>

            {/* Table Area */}
            <div className="flex-1 overflow-auto p-4 sm:p-6">
              {filteredUnits.length > 0 ? (
                <table className="w-full text-left text-xs">
                  <thead className="sticky top-0 bg-[#F5F3EE] z-10 border-b border-[#E7E2D6] text-slate-700 font-semibold">
                    <tr>
                      <th
                        onClick={() => handleSort("unitNumber")}
                        className="p-3 cursor-pointer hover:bg-slate-200/50 transition-colors select-none"
                      >
                        <div className="flex items-center gap-1">
                          Unit #
                          <ArrowUpDown className="w-3 h-3 text-slate-400" />
                        </div>
                      </th>
                      <th className="p-3">Bedroom Type</th>
                      <th
                        onClick={() => handleSort("floor")}
                        className="p-3 cursor-pointer hover:bg-slate-200/50 transition-colors select-none"
                      >
                        <div className="flex items-center gap-1">
                          Floor / Stack
                          <ArrowUpDown className="w-3 h-3 text-slate-400" />
                        </div>
                      </th>
                      <th
                        onClick={() => handleSort("sqft")}
                        className="p-3 cursor-pointer hover:bg-slate-200/50 transition-colors select-none"
                      >
                        <div className="flex items-center gap-1">
                          Sqft
                          <ArrowUpDown className="w-3 h-3 text-slate-400" />
                        </div>
                      </th>
                      <th
                        onClick={() => handleSort("price")}
                        className="p-3 cursor-pointer hover:bg-slate-200/50 transition-colors select-none"
                      >
                        <div className="flex items-center gap-1">
                          Price (CHF)
                          <ArrowUpDown className="w-3 h-3 text-slate-400" />
                        </div>
                      </th>
                      <th
                        onClick={() => handleSort("psf")}
                        className="p-3 cursor-pointer hover:bg-slate-200/50 transition-colors select-none"
                      >
                        <div className="flex items-center gap-1">
                          PSF
                          <ArrowUpDown className="w-3 h-3 text-slate-400" />
                        </div>
                      </th>
                      <th className="p-3">Status</th>
                      <th className="p-3 text-right">Floorplan</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E7E2D6]">
                    {filteredUnits.map((u, idx) => (
                      <tr key={idx} className="hover:bg-slate-50 transition-colors">
                        <td className="p-3 font-semibold text-[#0A1128]">{u.unitNumber}</td>
                        <td className="p-3 text-slate-700 font-medium">{u.bedroomType}</td>
                        <td className="p-3 text-slate-600">
                          {u.floor && u.stack ? `L${u.floor} / Stk ${u.stack}` : u.floor ? `Level ${u.floor}` : `Stack ${u.stack || '-'}`}
                        </td>
                        <td className="p-3 font-medium text-slate-800">
                          {u.sqft ? `${u.sqft.toLocaleString()} sqft` : 'TBC'}
                        </td>
                        <td className="p-3 font-semibold text-[#0A1128]">
                          {u.priceStr ? convertSgdText(u.priceStr, chfRate) : 'Price on Request'}
                        </td>
                        <td className="p-3 text-slate-600 font-medium">
                          {u.psfStr ? convertSgdText(u.psfStr, chfRate) : '-'}
                        </td>
                        <td className="p-3">
                          <span className="inline-block px-2 py-0.5 rounded text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium">
                            {u.status || "Available"}
                          </span>
                        </td>
                        <td className="p-3 text-right">
                          {u.floorplanUrl ? (
                            <button
                              onClick={() => setFloorplanPreviewUrl(cleanDriveUrl(u.floorplanUrl))}
                              className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold text-[#C5A880] bg-[#C5A880]/10 hover:bg-[#C5A880]/20 rounded border border-[#C5A880]/30 transition-colors"
                            >
                              <Eye className="w-3 h-3" /> View Floorplan
                            </button>
                          ) : (
                            <span className="text-[11px] text-slate-400 italic">On Request</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="py-12 text-center text-slate-500 text-xs">
                  No unsold units found matching your search criteria.
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-[#E7E2D6] bg-[#F5F3EE] flex items-center justify-between text-xs text-slate-600">
              <div>
                Showing <strong>{filteredUnits.length}</strong> of <strong>{availableUnitsList.length}</strong> available unsold units.
              </div>
              <Button
                onClick={() => setUnitsModalOpen(false)}
                className="bg-[#0A1128] text-white hover:bg-[#152243] text-xs font-semibold px-4 py-1.5 rounded-lg"
              >
                Close Modal
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Floorplan Preview Lightbox Modal */}
      {floorplanPreviewUrl && (
        <div
          onClick={() => setFloorplanPreviewUrl(null)}
          className="fixed inset-0 z-[300] bg-black/90 backdrop-blur-md flex items-center justify-center p-4"
        >
          <button
            onClick={() => setFloorplanPreviewUrl(null)}
            className="fixed top-6 right-6 text-white/80 hover:text-white p-2 text-2xl font-light z-[301]"
          >
            <X className="w-8 h-8" />
          </button>
          <div onClick={(e) => e.stopPropagation()} className="max-w-4xl max-h-[85vh] relative bg-white p-4 rounded-2xl shadow-2xl">
            <img
              src={cleanDriveUrl(floorplanPreviewUrl)}
              alt="Floorplan Preview"
              onError={handleImgFallback}
              className="max-w-full max-h-[80vh] object-contain rounded-xl"
            />
          </div>
        </div>
      )}
    </div>
  );
}
